<template>
  <div class="flex flex-col gap-y-4 text-left" data-testid="send-record-admin-adjustment">
    <div class="flex items-center justify-between gap-2">
      <p class="text-sm font-bold text-textPrimary">Ajuste administrativo</p>
      <span class="text-xs font-bold rounded-full bg-gray-100 text-gray-700 px-2 py-0.5">
        Somente auditoria
      </span>
    </div>

    <div class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <p class="text-sm text-textPrimary">{{ item?.doctorName || 'Plantão selecionado' }}</p>
      <p class="text-xs text-neutral50">{{ contextLine }}</p>
    </div>

    <label class="flex flex-col text-xs text-neutral50 max-w-[220px]">
      Equivale a *
      <select
        class="rounded border border-gray-200 px-2 py-1 text-sm text-textPrimary"
        :value="line.coveredLegs"
        :data-testid="`send-record-leg-select-${line.doctorStationId}`"
        @change="updateLeg($event)"
      >
        <option v-for="legs in coveredLegsOptions" :key="legs" :value="legs">
          {{ COVERED_LEGS_LABELS[legs] }}
        </option>
      </select>
    </label>

    <div class="flex flex-wrap items-end gap-3">
      <label class="flex flex-col text-xs text-neutral50">
        Check-in declarado
        <input
          type="time"
          class="rounded border border-gray-200 px-2 py-1 text-sm text-textPrimary"
          :value="isoToTimeInput(line.declaredCheckin)"
          :disabled="!covers(CoveredLegs.CHECKIN)"
          :data-testid="`send-record-checkin-${line.doctorStationId}`"
          @input="updateTime('declaredCheckin', $event)"
        />
        <span v-if="item?.expectedStart">previsto {{ isoToTimeInput(item.expectedStart) }}</span>
      </label>

      <label class="flex flex-col text-xs text-neutral50">
        Check-out declarado
        <input
          type="time"
          class="rounded border border-gray-200 px-2 py-1 text-sm text-textPrimary"
          :value="isoToTimeInput(line.declaredCheckout)"
          :disabled="!covers(CoveredLegs.CHECKOUT)"
          :data-testid="`send-record-checkout-${line.doctorStationId}`"
          @input="updateTime('declaredCheckout', $event)"
        />
        <span v-if="item?.expectedEnd">previsto {{ isoToTimeInput(item.expectedEnd) }}</span>
      </label>
    </div>

    <!--
      Balão ㉚: a justificativa obrigatória é o que separa "ajuste administrativo" de "edição
      manual sem rastro" — que é exatamente o que a F2 está fechando. Mesma régua de 20
      caracteres já em produção na rejeição da fila antiga e na reversão do pós-plantão.
    -->
    <div>
      <label class="text-sm font-bold text-textPrimary" for="send-record-admin-justification">
        Justificativa *
      </label>
      <textarea
        id="send-record-admin-justification"
        data-testid="send-record-admin-justification"
        rows="3"
        maxlength="500"
        class="w-full rounded border border-gray-200 px-2 py-1 text-sm"
        :value="description"
        @input="emit('update:description', ($event.target as HTMLTextAreaElement).value)"
      ></textarea>
      <p class="text-xs" :class="isLongEnough ? 'text-neutral50' : 'text-red-700'">
        Obrigatória, mínimo {{ minLength }} caracteres · {{ description.trim().length }}/500 · fica
        no histórico do plantão com o seu nome.
      </p>
    </div>

    <p class="rounded bg-primary5/40 border border-primary30 px-3 py-2 text-xs text-textPrimary">
      Este ajuste altera o horário efetivo do plantão. Fica registrado como fonte "Registro de
      Atividade", com o seu nome e a justificativa, e precisa ser aprovado por outra pessoa.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  CoveredLegs,
  COVERED_LEGS_LABELS,
  ACTIVITY_DESCRIPTION_MIN_LENGTH
} from '@/constants/ds-attendance.constants'
import {
  applyTimeInputToIso,
  isDescriptionLongEnough,
  isoToTimeInput,
  type NormalizedPrefillItem,
  type SendRecordLine
} from './types'

/**
 * Variação compacta de M2-2. Com o bloqueio TOTAL da inserção manual de
 * check-in/out (Q4/Q18), esta é a ÚNICA porta para a auditoria corrigir um
 * horário: um plantão, uma justificativa, um clique. O anexo é opcional (V7b) —
 * aqui o documento é a justificativa assinada pela auditoria.
 */

const props = defineProps<{
  item: NormalizedPrefillItem | null
  line: SendRecordLine
  description: string
}>()

const emit = defineEmits<{
  'update:line': [value: SendRecordLine]
  'update:description': [value: string]
}>()

const coveredLegsOptions = Object.values(CoveredLegs)
const minLength = ACTIVITY_DESCRIPTION_MIN_LENGTH

const isLongEnough = computed(() => isDescriptionLongEnough(props.description))

const contextLine = computed(() => {
  const item = props.item
  if (!item) return ''
  const hours =
    item.expectedStart && item.expectedEnd
      ? `${isoToTimeInput(item.expectedStart)}–${isoToTimeInput(item.expectedEnd)}`
      : ''
  return [item.healthUnitName, item.scheduleName, formatDate(item.expectedStart), hours]
    .filter(Boolean)
    .join(' · ')
})

function covers(leg: CoveredLegs): boolean {
  return props.line.coveredLegs === leg || props.line.coveredLegs === CoveredLegs.BOTH
}

function updateLeg(event: Event): void {
  emit('update:line', {
    ...props.line,
    coveredLegs: (event.target as HTMLSelectElement).value as CoveredLegs
  })
}

function updateTime(field: 'declaredCheckin' | 'declaredCheckout', event: Event): void {
  const base = field === 'declaredCheckin' ? props.item?.expectedStart : props.item?.expectedEnd
  emit('update:line', {
    ...props.line,
    [field]: applyTimeInputToIso(
      base ?? props.line[field],
      (event.target as HTMLInputElement).value
    )
  })
}

function formatDate(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('pt-BR')
}
</script>
